/**
 * Hook: useSpeechLanguage
 * SpecReferenceID: SPEECH-V1
 * Requirements: SPEECH-V1-R13, SPEECH-V1-R15
 *
 * Resolves and persists the language the user has declared they intend to
 * speak, for the streaming speech-to-text path.
 *
 * SPEECH-V1-R13: exposes the active language and the set the user may pick
 *                from, so the caller can always show which language the
 *                recogniser is listening for.
 * SPEECH-V1-R15: persists the choice. Identified users get it stored on
 *                their user record (PATCH /v1/users/me) so it follows them
 *                across devices; anonymous users get localStorage only,
 *                because every anonymous visitor resolves to one shared
 *                synthetic user row and a server-side write would change
 *                the language for all of them.
 *
 * Resolution order for the active language:
 *   1. the signed-in user's stored preference (server)
 *   2. this browser's last choice (localStorage)
 *   3. the deployment's configured default
 *
 * Risk Links: R8, R11
 * FMEA Links: FM4, FM11
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';

/** Browser-local fallback, and the only store for anonymous users. */
const STORAGE_KEY = 'galen-speech-language';

export interface SpeechLanguageOption {
  /** BCP-47 tag, e.g. "fr-FR". */
  code: string;
  /** Human-readable label, in the language itself where known. */
  label: string;
}

export interface UseSpeechLanguageOptions {
  /**
   * Whether the consumer can actually use a declared language. When false
   * the hook performs no network calls at all and reports an empty set.
   *
   * This exists because the hook is called from the shared chat/search
   * input, which mounts on surfaces where speech is disabled entirely.
   * Fetching regardless would put two requests behind every one of those
   * mounts for a control that is never rendered.
   */
  enabled?: boolean;
}

export interface UseSpeechLanguageResult {
  /** The language recognition will use for the next session. */
  language: string;
  /** Languages offered in the picker. */
  options: SpeechLanguageOption[];
  /** Change the language and persist the choice. */
  setLanguage: (code: string) => void;
  /** True until the configured set has been fetched. */
  isLoading: boolean;
}

function readStoredLanguage(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode, blocked storage, or a hardened browser profile. A
    // missing preference is a normal state, not an error - fall through to
    // the deployment default.
    return null;
  }
}

function writeStoredLanguage(code: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Non-fatal: the choice still applies to this session, it just will not
    // survive a reload. Failing the interaction over this would be worse.
  }
}

/**
 * Resolve, expose and persist the declared speech-recognition language.
 */
export function useSpeechLanguage(
  { enabled = true }: UseSpeechLanguageOptions = {}
): UseSpeechLanguageResult {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [supported, setSupported] = useState<SpeechLanguageOption[]>([]);
  const [defaultLanguage, setDefaultLanguage] = useState<string>('');
  const [language, setLanguageState] = useState<string>('');
  const [isLoading, setIsLoading] = useState(enabled);

  // Fetch the configured set, then the user's stored choice. Both are
  // best-effort: if either call fails the picker still works against
  // whatever we did manage to resolve, because losing the preference is a
  // far better outcome than losing the microphone.
  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function resolve(): Promise<void> {
      let configuredDefault = '';
      let configuredSupported: SpeechLanguageOption[] = [];

      try {
        const { data } = await api.get<{
          default_language?: string;
          languages?: { code?: string; label?: string }[];
        }>('/config/speech-languages');
        // Defensive about the response shape rather than trusting it: this
        // hook renders inside the chat input, so a malformed or stubbed
        // response must degrade to "no picker", never take the input area
        // down with it.
        configuredDefault =
          typeof data?.default_language === 'string' ? data.default_language : '';
        // SPEECH-V1-R16: labels are served rather than derived here, so an
        // administrator adding a language needs no frontend release. An
        // entry missing a label falls back to its own code.
        configuredSupported = Array.isArray(data?.languages)
          ? data.languages
              .filter(
                (l): l is { code: string; label?: string } =>
                  typeof l?.code === 'string' && l.code.length > 0
              )
              .map((l) => ({
                code: l.code,
                label: typeof l.label === 'string' && l.label ? l.label : l.code,
              }))
          : [];
      } catch {
        // Endpoint unreachable or the module is disabled. Leave the set
        // empty; the caller hides the picker when there is nothing to pick.
      }

      if (cancelled) return;

      setDefaultLanguage(configuredDefault);
      setSupported(configuredSupported);

      // Server preference wins for a signed-in user, because it is the one
      // that follows them between devices.
      let resolved = '';
      if (isAuthenticated) {
        try {
          const { data } = await api.get<{ speech_language?: string | null }>(
            '/users/me'
          );
          if (typeof data?.speech_language === 'string' && data.speech_language) {
            resolved = data.speech_language;
          }
        } catch {
          // Fall through to the local value.
        }
      }

      if (cancelled) return;

      if (!resolved) {
        resolved = readStoredLanguage() ?? '';
      }

      setLanguageState(resolved || configuredDefault);
      setIsLoading(false);
    }

    void resolve();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, enabled]);

  const setLanguage = useCallback(
    (code: string) => {
      setLanguageState(code);
      writeStoredLanguage(code);

      // SPEECH-V1-R15: only an identified user gets a server-side write.
      // The request is fire-and-forget - the selection has already taken
      // effect locally, and a failed sync must not block the user from
      // speaking. A rejected value (422) means the deployment's set changed
      // under us, which the next resolve() will reconcile.
      if (isAuthenticated) {
        void api
          .patch('/users/me', { speech_language: code })
          .catch(() => undefined);
      }
    },
    [isAuthenticated]
  );

  const options = useMemo<SpeechLanguageOption[]>(() => {
    const offered = Array.isArray(supported) ? [...supported] : [];
    // Show the active language even when it is not in the offered set - it
    // may have been set by an integrator, or an administrator may have
    // removed it since the user chose. Omitting it would leave the picker
    // displaying one language while the recogniser used another. Its own
    // code stands in for a label we were never served.
    if (language && !offered.some((o) => o.code === language)) {
      offered.push({ code: language, label: language });
    }
    return offered;
  }, [supported, language]);

  return {
    language: language || defaultLanguage,
    options,
    setLanguage,
    isLoading,
  };
}
